// src/components/Sidebar.jsx
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useUser, API_BASE } from "../context/UserContext";
import { getFollowingColor, BEST_CATEGORIES, SHARED_CAT_COLOR } from "../constants"; // eslint-disable-line no-unused-vars

const C = {
  primary:          "#655d54",
  primaryDim:       "#595149",
  primaryContainer: "#ede0d5",
  bg:               "#faf9f6",
  surfaceLow:       "#f4f4f0",
  container:        "#edeeea",
  onSurface:        "#2f3430",
  onSurfaceVariant: "#5c605c",
  outlineVariant:   "#8a8e8a",
};
const FH = "'Noto Serif', Georgia, serif";
const FL = "'Manrope', -apple-system, sans-serif";

// NAV_ITEMS moved inside component for i18n

function SectionLabel({ children }) {
  return (
    <p style={{
      fontFamily: FL, fontSize: 9, fontWeight: 700,
      textTransform: "uppercase", letterSpacing: "0.18em",
      color: C.outlineVariant, margin: "0 0 6px 2px",
    }}>
      {children}
    </p>
  );
}

export default function Sidebar({
  activeTab, onTabChange,
  personalPlaces, onDeletePersonalPlace,
  unreadCount,
  selectedFollowingIds, onToggleFollowing,
  followingList = [],
  sidebarWidth = 240,
  onPlaceSelect,
  onViewUserProfile,
  mapMode = "personal", onMapModeChange,
  sharedCategory, onSharedCategoryChange,
  sharedPlaces = [], onSharedPlaceSelect,
}) {
  const { t } = useTranslation();
  const { user } = useUser();
  const NAV_ITEMS = [
    { id: "map",       icon: "map",            label: t("tabs.home") },
    { id: "curation",  icon: "folder_special", label: t("tabs.my_place") },
    { id: "social",    icon: "auto_stories",   label: t("tabs.social") },
  ];
  const [folders, setFolders] = useState([]);
  const [placeFilter, setPlaceFilter] = useState("");
  const [sortMode, setSortMode] = useState("collection"); // "newest" | "oldest" | "collection"
  useEffect(() => {
    if (!user) return;
    const seeded = sessionStorage.getItem(`folders_seeded_${user.user_id}`);
    const fetchFolders = () =>
      axios.get(`${API_BASE}/folders/`)
        .then((res) => setFolders(res.data)).catch(() => {});
    if (!seeded) {
      axios.post(`${API_BASE}/folders/seed-defaults`)
        .then(() => sessionStorage.setItem(`folders_seeded_${user.user_id}`, "1"))
        .catch(() => {})
        .finally(fetchFolders);
    } else {
      fetchFolders();
    }
  }, [user]);

  const getFolderColor = (folderId) => {
    if (!folderId) return C.outlineVariant;
    const f = folders.find((x) => x.id === folderId);
    return f?.color || C.outlineVariant;
  };

  return (
    <aside style={{
      width: sidebarWidth,
      height: "100vh",
      position: "fixed",
      left: 0, top: 0,
      zIndex: 40,
      background: C.bg,
      display: "flex",
      flexDirection: "column",
      padding: "20px 20px 60px",
      overflowY: "auto",
      overflowX: "hidden",
      boxSizing: "border-box",
      boxShadow: "1px 0 0 rgba(101,93,84,0.07)",
    }}>

      {/* ── Brand + Mode toggle (active mode on top) ──────────── */}
      <div data-tutorial="map-mode-toggle" style={{ padding: "0 6px", marginBottom: 14, flexShrink: 0, display: "flex", alignItems: "center", gap: 2 }}>
        <img src="/logo.svg" alt="" style={{ width: 50, height: 50, flexShrink: 0, position: "relative", top: -1 }} />
        <div style={{ display: "flex", flexDirection: "column" }}>
          {[
            { mode: "personal", label: t("map.my_space") },
            { mode: "shared", label: t("map.our_space") },
          ]
            .sort((a, b) => (a.mode === mapMode ? -1 : 1))
            .map(({ mode, label }) => {
              const isActive = mode === mapMode;
              return (
                <p
                  key={mode}
                  onClick={() => { onMapModeChange && onMapModeChange(mode); onTabChange("map"); }}
                  style={{
                    fontFamily: FH, fontStyle: "italic",
                    fontSize: isActive ? 18 : 14,
                    margin: isActive ? "0 0 1px" : 0,
                    letterSpacing: "-0.02em",
                    cursor: "pointer",
                    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                    color: isActive ? C.primary : C.outlineVariant,
                    fontWeight: isActive ? 700 : 400,
                    order: isActive ? 0 : 1,
                  }}
                >
                  {label}
                </p>
              );
            })}
        </div>
      </div>

      {/* ── Navigation ─────────────────────────────────────── */}
      <nav style={{ display: "flex", flexDirection: "column", gap: 0, marginBottom: 20, flexShrink: 0 }}>
        {NAV_ITEMS.map((item) => {
          const isActive = activeTab === item.id;
          const hasUnread = item.id === "news" && unreadCount > 0;
          return (
            <button
              key={item.id}
              data-tutorial={`tab-${item.id}`}
              onClick={() => onTabChange(item.id)}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "7px 10px",
                border: "none", cursor: "pointer",
                borderRadius: 7,
                background: isActive ? C.primaryContainer : "transparent",
                color: isActive ? C.primary : C.onSurfaceVariant,
                fontFamily: FL, fontSize: 11,
                fontWeight: isActive ? 700 : 500,
                textTransform: "uppercase", letterSpacing: "0.07em",
                transform: isActive ? "translateX(3px)" : "translateX(0)",
                transition: "all 0.16s ease",
                position: "relative", textAlign: "left",
                WebkitTapHighlightColor: "transparent",
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = C.surfaceLow;
                  e.currentTarget.style.transform = "translateX(3px)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.transform = "translateX(0)";
                }
              }}
            >
              <span className="material-symbols-outlined" style={{
                fontSize: 17, flexShrink: 0,
                fontVariationSettings: isActive
                  ? "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24"
                  : "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24",
              }}>
                {item.icon}
              </span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {hasUnread && (
                <span style={{
                  width: 14, height: 14, borderRadius: "50%",
                  background: "#9e422c", color: "white",
                  fontFamily: FL, fontSize: 8, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* ── Map tab content (shared mode) ───────────────────── */}
      {activeTab === "map" && mapMode === "shared" && (
        <div style={{
          flex: 1, display: "flex", flexDirection: "column",
          paddingTop: 4, minHeight: 0, gap: 12,
          animation: "sidebarFade 0.15s ease",
        }}>
          {/* Category filter */}
          <div>
            <SectionLabel>Categories</SectionLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <button
                onClick={() => onSharedCategoryChange(null)}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "6px 10px", border: "none", borderRadius: 7,
                  background: sharedCategory === null ? C.primaryContainer : "transparent",
                  color: sharedCategory === null ? C.primary : C.onSurfaceVariant,
                  fontFamily: FL, fontSize: 11, fontWeight: sharedCategory === null ? 700 : 500,
                  cursor: "pointer", textAlign: "left", transition: "all 0.15s",
                }}
              >All</button>
              {BEST_CATEGORIES.map((cat) => {
                const catColor = SHARED_CAT_COLOR[cat.key] || C.primary;
                const isActive = sharedCategory === cat.key;
                return (
                  <button
                    key={cat.key}
                    onClick={() => onSharedCategoryChange(cat.key)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "6px 10px", border: "none", borderRadius: 7,
                      background: isActive ? `${catColor}18` : "transparent",
                      color: isActive ? catColor : C.onSurfaceVariant,
                      fontFamily: FL, fontSize: 11, fontWeight: isActive ? 700 : 500,
                      cursor: "pointer", textAlign: "left", transition: "all 0.15s",
                    }}
                  >{cat.emoji} {cat.label}</button>
                );
              })}
            </div>
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: C.container }} />

          {/* Popular places list — grouped by category */}
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <SectionLabel>{t("map.popular_places")}</SectionLabel>
            <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {sharedPlaces.length === 0 ? (
                <p style={{ fontFamily: FL, fontSize: 11, color: C.outlineVariant, margin: "8px 4px", fontStyle: "italic" }}>
                  {t("map.no_top_picks")}
                </p>
              ) : (() => {
                const grouped = {};
                sharedPlaces.forEach((p) => {
                  (grouped[p.category] = grouped[p.category] || []).push(p);
                });
                const catOrder = BEST_CATEGORIES.map((c) => c.key);
                const catLabel = Object.fromEntries(BEST_CATEGORIES.map((c) => [c.key, c.label]));
                return catOrder.filter((c) => grouped[c]).map((cat) => (
                  <div key={cat} style={{ marginBottom: 10 }}>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 5,
                      padding: "4px 4px 2px", marginBottom: 2,
                    }}>
                      <div style={{ width: 3, height: 12, borderRadius: 2, background: SHARED_CAT_COLOR[cat], flexShrink: 0 }} />
                      <p style={{
                        margin: 0, fontFamily: FL, fontSize: 9, fontWeight: 700,
                        textTransform: "uppercase", letterSpacing: "0.1em",
                        color: C.outlineVariant,
                      }}>{catLabel[cat]} ({grouped[cat].length})</p>
                    </div>
                    {grouped[cat].map((p, i) => (
                      <div
                        key={`${p.google_place_id || i}_${cat}`}
                        onClick={() => onSharedPlaceSelect && onSharedPlaceSelect(p)}
                        style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "4px 6px", borderRadius: 6,
                          cursor: "pointer", transition: "background 0.12s",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = C.surfaceLow}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <span style={{
                          fontFamily: FL, fontSize: 11, fontWeight: 800,
                          color: SHARED_CAT_COLOR[cat], flexShrink: 0, minWidth: 24,
                        }}>({p.pick_count})</span>
                        <p style={{
                          margin: 0, fontFamily: FL, fontSize: 11, fontWeight: 600,
                          color: C.onSurface, flex: 1,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>{p.name}</p>
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {activeTab === "map" && mapMode === "personal" && (
        <div style={{
          flex: 1, display: "flex", flexDirection: "column",
          paddingTop: 4, minHeight: 0, gap: 12,
          animation: "sidebarFade 0.15s ease",
        }}>
          {/* My Places section */}
          <div style={{
            flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <p style={{
                fontFamily: "'Manrope', -apple-system, sans-serif", fontSize: 9, fontWeight: 700,
                textTransform: "uppercase", letterSpacing: "0.18em",
                color: "#8a8e8a", margin: 0,
              }}>
                My Places ({personalPlaces.length})
              </p>
              <div style={{ display: "flex", gap: 2 }}>
                {[
                  { key: "newest", icon: "arrow_downward", title: "Newest first" },
                  { key: "oldest", icon: "arrow_upward", title: "Oldest first" },
                  { key: "collection", icon: "folder", title: "By collection" },
                ].map((s) => (
                  <button
                    key={s.key}
                    title={s.title}
                    onClick={() => setSortMode(s.key)}
                    style={{
                      background: sortMode === s.key ? "#ede0d5" : "transparent",
                      border: "none", borderRadius: 4, padding: "2px 4px",
                      cursor: "pointer", display: "flex", alignItems: "center",
                      transition: "background 0.15s",
                    }}
                  >
                    <span className="material-symbols-outlined" style={{
                      fontSize: 12,
                      color: sortMode === s.key ? "#655d54" : "#8a8e8a",
                      fontVariationSettings: "'FILL' 0, 'wght' 300",
                    }}>{s.icon}</span>
                  </button>
                ))}
              </div>
            </div>
            {personalPlaces.length > 5 && (
              <div style={{ position: "relative", marginBottom: 6, flexShrink: 0 }}>
                <span className="material-symbols-outlined" style={{
                  position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)",
                  fontSize: 12, color: C.outlineVariant, pointerEvents: "none",
                }}>search</span>
                <input
                  value={placeFilter}
                  onChange={(e) => setPlaceFilter(e.target.value)}
                  placeholder={t("map.search_my_places")}
                  style={{
                    width: "100%", padding: "4px 6px 4px 22px",
                    background: C.surfaceLow, border: "none", borderRadius: 5,
                    fontFamily: FL, color: C.onSurface,
                    outline: "none", boxSizing: "border-box",
                  }}
                  ref={(el) => { if (el) el.style.setProperty("font-size", "12px", "important"); }}
                />
              </div>
            )}
            <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {(() => {
                const filtered = personalPlaces
                  .filter((p) => !placeFilter || p.name.toLowerCase().includes(placeFilter.toLowerCase()));
                const timeSort = sortMode === "oldest"
                  ? (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
                  : (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);

                const PlaceRow = ({ p }) => (
                  <div key={p.id}
                    onClick={() => onPlaceSelect && onPlaceSelect(p)}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "4px 4px", borderRadius: 6,
                      cursor: "pointer", transition: "background 0.12s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = C.surfaceLow}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >
                    {sortMode !== "collection" && <div style={{
                      width: 3, height: 18, borderRadius: 2,
                      background: getFolderColor(p.folder_id), flexShrink: 0,
                    }} />}
                    <p style={{
                      margin: 0, fontFamily: FL, fontSize: 11, fontWeight: 600,
                      color: C.onSurface, flex: 1,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {p.name}
                    </p>
                    <button
                      onClick={(e) => { e.stopPropagation(); onDeletePersonalPlace(p.id); }}
                      style={{
                        background: "none", border: "none",
                        fontFamily: FL, fontSize: 9, color: C.outlineVariant,
                        cursor: "pointer", padding: "2px 4px",
                        transition: "color 0.15s",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = "#9e422c"}
                      onMouseLeave={(e) => e.currentTarget.style.color = C.outlineVariant}
                    >
                      Remove
                    </button>
                  </div>
                );

                if (sortMode === "collection") {
                  // Group by collection
                  const grouped = {};
                  filtered.forEach((p) => {
                    const fid = p.folder_id || 0;
                    (grouped[fid] = grouped[fid] || []).push(p);
                  });
                  // Folder order: follows folders array order; uncategorized (0) goes last
                  const orderedFids = folders.map((f) => f.id).filter((id) => grouped[id]);
                  if (grouped[0]) orderedFids.push(0);
                  return orderedFids.length === 0 ? (
                    <p style={{ fontFamily: FL, fontSize: 11, color: C.outlineVariant, margin: "8px 4px" }}>
                      No saved places yet
                    </p>
                  ) : orderedFids.map((fid) => {
                    const folder = folders.find((f) => f.id === fid);
                    const color = folder?.color || C.outlineVariant;
                    const name = folder?.name || "Uncategorized";
                    const items = grouped[fid].sort(timeSort);
                    return (
                      <div key={fid} style={{ marginBottom: 10 }}>
                        <div style={{
                          display: "flex", alignItems: "center", gap: 5,
                          padding: "4px 4px 2px", marginBottom: 2,
                        }}>
                          <div style={{ width: 3, height: 12, borderRadius: 2, background: color, flexShrink: 0 }} />
                          <p style={{
                            margin: 0, fontFamily: FL, fontSize: 9, fontWeight: 700,
                            textTransform: "uppercase", letterSpacing: "0.1em",
                            color: C.outlineVariant,
                          }}>{name} ({items.length})</p>
                        </div>
                        {items.map((p) => <PlaceRow key={p.id} p={p} />)}
                      </div>
                    );
                  });
                }

                // newest / oldest
                return filtered.length === 0 ? (
                  <p style={{ fontFamily: FL, fontSize: 11, color: C.outlineVariant, margin: "8px 4px" }}>
                    No saved places yet
                  </p>
                ) : filtered.sort(timeSort).slice(0, 50).map((p) => <PlaceRow key={p.id} p={p} />);
              })()}
            </div>
          </div>

          {/* Following layer */}
          {followingList.length > 0 && (
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <div style={{ padding: "2px 2px" }}>
                <SectionLabel>Following ({followingList.length})</SectionLabel>
              </div>
              <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                {followingList.map((f, idx) => {
                  const color = getFollowingColor(idx);
                  const isSelected = selectedFollowingIds.includes(f.id);
                  return (
                    <div
                      key={f.id}
                      style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "5px 8px",
                        borderRadius: 7,
                        background: isSelected ? `${color}14` : "transparent",
                        marginBottom: 2,
                        transition: "background 0.15s",
                      }}
                    >
                      {/* Avatar + nickname — opens profile (left side) */}
                      <div
                        onClick={() => onViewUserProfile && onViewUserProfile(f.nickname)}
                        style={{
                          display: "flex", alignItems: "center", gap: 8,
                          flex: 1, minWidth: 0, cursor: "pointer",
                          borderRadius: 5, padding: "2px 0",
                        }}
                      >
                        <div style={{
                          width: 20, height: 20, borderRadius: "50%",
                          background: color, flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontFamily: FL, fontSize: 9, color: "white", fontWeight: 700,
                        }}>
                          {f.nickname?.[0]?.toUpperCase()}
                        </div>
                        <span style={{
                          fontFamily: FL, fontSize: 11, color: C.onSurface,
                          flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {f.nickname}
                        </span>
                      </div>
                      {/* Checkbox — toggle map layer (right side) */}
                      <div
                        onClick={() => onToggleFollowing(f.id)}
                        style={{
                          width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                          background: isSelected ? color : C.container,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: "pointer", transition: "background 0.15s",
                        }}
                      >
                        {isSelected && <span style={{ color: "white", fontSize: 9, fontWeight: 700 }}>✓</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── User card (bottom) — fixed to bottom like map buttons ── */}
      <div style={{
        position: "fixed", bottom: 0, left: 0,
        width: sidebarWidth, boxSizing: "border-box",
        padding: "10px 20px 16px",
        background: C.bg,
        zIndex: 41,
      }}>
        {user ? (
          <div
            data-tutorial="tab-profile"
            onClick={() => onTabChange("profile")}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 10px",
              background: activeTab === "profile" ? C.primaryContainer : C.surfaceLow,
              borderRadius: 8,
              cursor: "pointer",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => { if (activeTab !== "profile") e.currentTarget.style.background = C.container; }}
            onMouseLeave={(e) => { if (activeTab !== "profile") e.currentTarget.style.background = C.surfaceLow; }}
          >
            <div style={{
              width: 26, height: 26, borderRadius: "50%",
              background: `linear-gradient(135deg, ${C.primaryDim}, ${C.primary})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: FH, fontStyle: "italic",
              fontSize: 13, color: "#fff6ef", fontWeight: 700, flexShrink: 0,
            }}>
              {user.nickname?.[0]?.toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                margin: 0, fontFamily: FL, fontSize: 11, fontWeight: 600,
                color: activeTab === "profile" ? C.primary : C.onSurface,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {user.nickname}
              </p>
            </div>
            <span className="material-symbols-outlined" style={{ fontSize: 16, color: C.outlineVariant }}>
              chevron_right
            </span>
          </div>
        ) : (
          <div
            onClick={() => onTabChange("profile")}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px",
              background: C.primaryContainer,
              borderRadius: 8,
              cursor: "pointer",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#e5d6c9"}
            onMouseLeave={(e) => e.currentTarget.style.background = C.primaryContainer}
          >
            <span className="material-symbols-outlined" style={{
              fontSize: 18, color: C.primary,
              fontVariationSettings: "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24",
            }}>
              login
            </span>
            <span style={{
              fontFamily: FL, fontSize: 12, fontWeight: 700,
              color: C.primary,
            }}>
              Sign in / Sign up
            </span>
          </div>
        )}
      </div>
      <style>{`
        @keyframes sidebarFade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </aside>
  );
}
